/**
 * VirtualTourHero — Weidner Via6 page hero for /virtual-tour.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Virtual tour hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function VirtualTourHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="virtualtourhero.title"
        bgPath="virtualtourhero.bg_image"
        bgDef={bgDef}
        defaultTitle="Virtual Tour"
        keywords={['tour', 'virtual', '3d', 'walkthrough', 'apartment', 'interior']}
        pageHint="virtualtourhero"
        altText="Virtual tour hero image"
      />

      <RichTextContent
        path="virtualtourhero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="virtualtourhero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourHero',
  displayName: 'Virtual Tour Hero',
  tokens: {
    'virtualtourhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Virtual Tour',
    },
  },
  slotGroups: {
    'virtualtourhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Virtual tour hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(VirtualTourHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourHero;
